/**
 * Filters and cleans city name input according to validation rules
 * @param {string} value - The input value to filter
 * @returns {string} - The filtered and cleaned value
 */
export const filterCityName = (value) => {
  if (!value) return "";

  // Filter invalid characters (keep only letters, spaces, hyphens, apostrophes)
  let filteredValue = value.replace(/[^A-Za-zÀ-ÿ\s'-]/g, "");

  // Prevent multiple consecutive spaces
  filteredValue = filteredValue.replace(/\s{2,}/g, " ");

  // Prevent multiple consecutive hyphens
  filteredValue = filteredValue.replace(/-{2,}/g, "-");

  // Prevent multiple consecutive apostrophes
  filteredValue = filteredValue.replace(/'{2,}/g, "'");

  // Prevent apostrophe-hyphen combinations
  filteredValue = filteredValue.replace(/'-/g, "'");
  filteredValue = filteredValue.replace(/-'/g, "-");

  // Remove leading apostrophes or hyphens
  filteredValue = filteredValue.replace(/^['-]+/, "");

  // Limit to 50 characters
  filteredValue = filteredValue.slice(0, 50);

  return filteredValue;
};

/**
 * Creates a city name validation schema for Yup
 * @param {object} messages - Validation messages object
 * @param {object} REGEX_EXPS - Regular expressions object
 * @returns {object} - Yup validation schema
 */
export const createCityNameValidation = (messages, REGEX_EXPS) => {
  const yup = require("yup");

  return yup
    .string()
    .typeError(messages.REQUIRED)
    .required(messages.REQUIRED)
    .min(2, messages.INVALID_CITY_NAME)
    .max(50, messages.INVALID_CITY_NAME)
    .transform((value) => value?.trim())
    .matches(REGEX_EXPS.cityName, messages.INVALID_CITY_NAME)
    .test(
      "no-multiple-spaces",
      messages.INVALID_CITY_NAME,
      (value) => !value || !/\s{2,}/.test(value),
    )
    .test(
      "no-multiple-hyphens",
      messages.INVALID_CITY_NAME,
      (value) => !value || !/-{2,}/.test(value),
    )
    .test(
      "no-multiple-apostrophes",
      messages.INVALID_CITY_NAME,
      (value) => !value || !/'{2,}/.test(value),
    )
    .test(
      "no-apostrophe-hyphen-combo",
      messages.INVALID_CITY_NAME,
      (value) => !value || (!/'[-]/.test(value) && !/-[']/.test(value)),
    )
    .test(
      "no-leading-special-chars",
      messages.INVALID_CITY_NAME,
      (value) => !value || !/^['-]/.test(value),
    )
    .test(
      "no-trailing-special-chars",
      messages.INVALID_CITY_NAME,
      (value) => !value || !/['-]$/.test(value),
    );
};

/**
 * Filters and cleans contract number input according to validation rules
 * @param {string} value - The input value to filter
 * @returns {string} - The filtered and cleaned value
 */
export const filterContractNumber = (value) => {
  if (!value) return "";

  // Filter invalid characters (keep only letters, numbers, spaces, hyphens)
  let filteredValue = value.replace(/[^A-Za-z0-9\s-]/g, "");

  // Prevent multiple consecutive spaces
  filteredValue = filteredValue.replace(/\s{2,}/g, " ");

  // Prevent multiple consecutive hyphens
  filteredValue = filteredValue.replace(/-{2,}/g, "-");

  // Remove leading hyphens
  filteredValue = filteredValue.replace(/^-+/, "");

  return filteredValue;
};

/**
 * Creates a contract number validation schema for Yup
 * @param {object} messages - Validation messages object
 * @param {object} REGEX_EXPS - Regular expressions object
 * @returns {object} - Yup validation schema
 */
export const createContractNumberValidation = (messages, REGEX_EXPS) => {
  const yup = require("yup");

  return yup
    .string()
    .typeError(messages.REQUIRED)
    .required(messages.REQUIRED)
    .matches(REGEX_EXPS.contractNumber, messages.INVALID_CONTRACT_NUMBER)
    .test(
      "no-multiple-spaces",
      messages.INVALID_CONTRACT_NUMBER,
      (value) => !value || !/\s{2,}/.test(value),
    )
    .test(
      "no-multiple-hyphens",
      messages.INVALID_CONTRACT_NUMBER,
      (value) => !value || !/-{2,}/.test(value),
    )
    .test(
      "no-leading-hyphens",
      messages.INVALID_CONTRACT_NUMBER,
      (value) => !value || !/^-+/.test(value),
    )
    .test(
      "no-trailing-hyphens",
      messages.INVALID_CONTRACT_NUMBER,
      (value) => !value || !/-+$/.test(value),
    );
};
